import { useState, useEffect } from 'react';
import { getStoryTemplates } from '../services/storyApi';

// Module-level cache (persists across component mounts)
let cachedManifest = null;
let fetchPromise = null;

/**
 * Hook to fetch and cache the template manifest from the API.
 * The manifest is fetched once and cached at module level.
 *
 * @returns {{ manifest: Object|null, loading: boolean, error: string }}
 */
const useTemplateManifest = () => {
  const [manifest, setManifest] = useState(cachedManifest);
  const [loading, setLoading] = useState(!cachedManifest);
  const [error, setError] = useState('');

  useEffect(() => {
    if (cachedManifest) {
      setManifest(cachedManifest);
      setLoading(false);
      return;
    }

    // Deduplicate concurrent fetches
    if (!fetchPromise) {
      fetchPromise = getStoryTemplates()
        .then((data) => {
          cachedManifest = data;
          fetchPromise = null;
          return data;
        })
        .catch((err) => {
          fetchPromise = null;
          throw err;
        });
    }

    fetchPromise
      .then((data) => {
        setManifest(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load template manifest:', err);
        setError('Erreur de chargement des templates');
        setLoading(false);
      });
  }, []);

  return { manifest, loading, error };
};

/**
 * Get templates available for an event type from the manifest.
 * @param {Object} manifest - The manifest object
 * @param {string} eventType - Event type id
 * @returns {Array<{id, name, desc}>}
 */
export const getTemplatesForEvent = (manifest, eventType) => {
  if (!manifest?.templates) return [];
  return Object.entries(manifest.templates)
    .filter(([, tpl]) => tpl.eventTypes?.includes(eventType))
    .map(([id, tpl]) => ({
      id,
      name: tpl.name,
      desc: tpl.description,
    }));
};

/**
 * Get color defaults for a template from the manifest.
 * @param {Object} manifest - The manifest object
 * @param {string} templateId - Template id
 * @returns {Object|null} Color defaults
 */
export const getTemplateColors = (manifest, templateId) => {
  if (!manifest?.templates) return null;
  return manifest.templates[templateId]?.colors || null;
};

/**
 * Get event type defaults from the manifest.
 * @param {Object} manifest - The manifest object
 * @param {string} eventType - Event type id
 * @returns {Object|null}
 */
export const getEventDefaults = (manifest, eventType) => {
  if (!manifest?.eventTypes) return null;
  return manifest.eventTypes[eventType] || null;
};

/**
 * Get template options definition from manifest.
 * @param {Object} manifest - The manifest object
 * @param {string} templateId - Template id
 * @returns {Object} Options definition (may be empty)
 */
export const getTemplateOptions = (manifest, templateId) => {
  if (!manifest?.templates) return {};
  return manifest.templates[templateId]?.options || {};
};

export default useTemplateManifest;
